import { useMemo, useState } from "react";
import { formatRelativeTime } from "@/lib/format";
import type { ActivityEvent, ActivityFilter } from "@/types";

interface ActivityTimelineProps {
  events: ActivityEvent[];
  loading?: boolean;
}

const filters: { id: ActivityFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "deployments", label: "Deployments" },
  { id: "runtime", label: "Runtime" },
  { id: "backups", label: "Backups" },
  { id: "ai", label: "AI" },
  { id: "system", label: "System" },
];

const categoryIcon: Record<ActivityEvent["category"], string> = {
  deployment: "🚀",
  runtime: "🩺",
  backup: "💾",
  ai: "🤖",
  system: "⚙️",
};

function matchesFilter(event: ActivityEvent, filter: ActivityFilter): boolean {
  if (filter === "all") return true;
  if (filter === "deployments") return event.category === "deployment";
  if (filter === "runtime") return event.category === "runtime";
  if (filter === "backups") return event.category === "backup";
  if (filter === "ai") return event.category === "ai";
  if (filter === "system") return event.category === "system";
  return true;
}

export function ActivityTimeline({ events, loading }: ActivityTimelineProps) {
  const [filter, setFilter] = useState<ActivityFilter>("all");

  const filtered = useMemo(
    () => events.filter((event) => matchesFilter(event, filter)),
    [events, filter],
  );

  if (loading) {
    return (
      <div className="glass-panel activity-empty" aria-busy="true">
        Loading activity from platform records…
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="glass-panel activity-empty">
        No data available — no recorded deployments, scans, or backups yet.
      </div>
    );
  }

  return (
    <div className="glass-panel activity-panel">
      <div className="activity-panel__filters" role="tablist" aria-label="Filter activity">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            role="tab"
            aria-selected={filter === f.id}
            className={`filter-chip${filter === f.id ? " is-active" : ""}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="activity-empty">No activity in this category.</div>
      ) : (
        <ul className="activity-timeline" aria-label="Recent platform activity">
          {filtered.map((event) => (
            <li key={event.id} className="activity-item">
              <span className="activity-item__icon" aria-hidden="true">
                {categoryIcon[event.category]}
              </span>
              <div>
                <div className="activity-item__title">{event.title}</div>
                <div className="activity-item__detail">
                  {event.detail}
                  {event.tenantSlug ? ` · ${event.tenantSlug}` : null}
                </div>
              </div>
              <time className="activity-item__time" dateTime={event.timestamp}>
                {formatRelativeTime(event.timestamp)}
              </time>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
